import * as React from 'react';

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

export function Sheet({
  open,
  onOpenChange,
  title,
  children,
  className,
}: SheetProps) {
  const { t } = useTranslation();
  return (
    <DialogPrimitive.Root
      data-slot="sheet"
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogPrimitive.Portal data-slot="sheet-portal">
        <DialogPrimitive.Backdrop
          data-slot="sheet-overlay"
          className="data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs"
        />
        <DialogPrimitive.Popup
          data-slot="sheet-content"
          className={cn(
            'preflight',
            'bg-background data-open:animate-in data-closed:animate-out data-open:slide-in-from-right data-closed:slide-out-to-right ring-foreground/10 fixed inset-y-0 right-0 z-50 flex w-full flex-col gap-4 p-4 text-sm shadow-lg ring-1 duration-200 outline-none sm:max-w-sm',
            className,
          )}
        >
          <div className="flex items-center justify-between">
            <DialogPrimitive.Title
              data-slot="sheet-title"
              className="text-base leading-none font-medium"
            >
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              data-slot="sheet-close"
              render={
                <Button variant="ghost" size="icon-sm">
                  <XIcon />
                  <span className="sr-only">{t('common.close')}</span>
                </Button>
              }
            />
          </div>
          <div
            data-slot="sheet-body"
            className="-mx-4 flex-grow overflow-y-auto px-4"
          >
            {children}
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
